import React from "react";
import { Link } from "react-router-dom";

export function RefundPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Refund Policy</h1>
        <p className="text-slate-500 mb-8">Last updated: December 18, 2025</p>

        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Overview</h2>
            <p className="text-slate-600 leading-relaxed">
              At Calyro, we stand behind the quality of our AI voice agent services. This policy outlines our refund terms for setup fees, subscriptions, and overages.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Setup Fee Refunds</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-amber-800 font-medium">Setup fees are mandatory and cover white-glove onboarding.</p>
            </div>
            <div className="text-slate-600 leading-relaxed space-y-4">
              <p><strong>Before Go-Live (within 7 days):</strong> Full refund available if you cancel before your AI agent goes live and no calls have been processed.</p>
              <p><strong>After Go-Live:</strong> Setup fees are non-refundable once your agent is live and processing calls. This is because the setup work (configuration, voice tuning, QA testing) has been completed.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Monthly Subscription Refunds</h2>
            <div className="text-slate-600 leading-relaxed space-y-4">
              <p><strong>No Prorated Refunds:</strong> Monthly subscriptions are billed in advance and are non-refundable. When you cancel, you retain access until the end of your current billing period.</p>
              <p><strong>Cancellation:</strong> You may cancel at any time through your dashboard or by contacting support. Your service continues until the end of the paid period.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Yearly Subscription Refunds</h2>
            <div className="text-slate-600 leading-relaxed space-y-4">
              <p><strong>Within 30 Days:</strong> If you cancel within 30 days of your yearly subscription purchase, you may be eligible for a prorated refund minus any usage charges.</p>
              <p><strong>After 30 Days:</strong> Yearly subscriptions are non-refundable after 30 days. When you cancel, service continues until the end of your yearly term.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Overage Charges</h2>
            <div className="text-slate-600 leading-relaxed space-y-4">
              <p><strong>Non-Refundable:</strong> Overage charges for minutes used beyond your plan's included minutes are non-refundable, as this usage has already been consumed.</p>
              <p><strong>Disputes:</strong> If you believe there is an error in your usage calculation, contact support within 30 days of the charge for review.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Refund Eligibility Summary</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left p-3 font-semibold text-slate-700">Charge Type</th>
                    <th className="text-left p-3 font-semibold text-slate-700">Condition</th>
                    <th className="text-left p-3 font-semibold text-slate-700">Refund</th>
                  </tr>
                </thead>
                <tbody className="text-slate-600">
                  <tr className="border-b">
                    <td className="p-3">Setup Fee</td>
                    <td className="p-3">Before go-live (within 7 days)</td>
                    <td className="p-3 text-green-600 font-medium">Full refund</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3">Setup Fee</td>
                    <td className="p-3">After go-live</td>
                    <td className="p-3 text-red-600 font-medium">No refund</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3">Monthly Subscription</td>
                    <td className="p-3">Any time</td>
                    <td className="p-3 text-red-600 font-medium">No prorated refund</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3">Yearly Subscription</td>
                    <td className="p-3">Within 30 days</td>
                    <td className="p-3 text-green-600 font-medium">Prorated refund</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3">Yearly Subscription</td>
                    <td className="p-3">After 30 days</td>
                    <td className="p-3 text-red-600 font-medium">No refund</td>
                  </tr>
                  <tr>
                    <td className="p-3">Overage Charges</td>
                    <td className="p-3">Any time</td>
                    <td className="p-3 text-red-600 font-medium">No refund</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">How to Request a Refund</h2>
            <div className="text-slate-600 leading-relaxed space-y-4">
              <p>To request a refund:</p>
              <ol className="list-decimal list-inside space-y-2">
                <li>Email support@calyro.ai with subject "Refund Request"</li>
                <li>Include your account email and reason for refund</li>
                <li>We will respond within 2 business days</li>
                <li>Approved refunds are processed within 5-10 business days</li>
              </ol>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Service Issues</h2>
            <p className="text-slate-600 leading-relaxed">
              If you experience service issues or quality problems, please contact support before requesting a refund. We are committed to making things right and may offer service credits, extended trials, or other resolutions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Contact Us</h2>
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-slate-700 font-medium">Calyro Support</p>
              <p className="text-slate-600">Email: support@calyro.ai</p>
              <p className="text-slate-600">Response Time: Within 24 hours</p>
            </div>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-blue-600 hover:underline">← Back to Home</Link>
        </div>
      </div>
    </div>
  );
}
